import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingField } from "@/components/settings/setting-field";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import type { SaveField } from "../../../hooks/use-auto-save-field";
import type { RewardsConfig } from "../hooks/use-rewards-config";

export function PointsEarningSettings({
  saveField,
  saving,
}: {
  saveField: SaveField<RewardsConfig>;
  saving: boolean;
}) {
  const { control } = useFormContext<RewardsConfig>();
  const { t } = useTranslation("settings");
  const loyaltyProgramEnabled = useWatch({ control, name: "loyaltyProgramEnabled" });

  if (!loyaltyProgramEnabled) {
    return (
      <div className="p-4 bg-muted/50 rounded-lg text-center text-muted-foreground">
        {t("rewards.loyaltyProgramDisabledMessage")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Enable Points Earning */}
      <FormField
        control={control}
        name="enabled"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between">
            <SettingField
              label={t("rewards.letCustomersEarnPoints")}
              helpText={t("rewards.fields.pointsEarning.helpText")}
              helpDetails={
                t("rewards.fields.pointsEarning.helpDetails", { returnObjects: true }) as {
                  title: string;
                  benefits: string[];
                }
              }
              className="flex-1"
            />
            <FormControl>
              <Switch
                checked={field.value}
                disabled={saving}
                onCheckedChange={(value) => {
                  void saveField("enabled", value);
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <PointsConfiguration />
    </div>
  );
}

function PointsConfiguration() {
  const { control } = useFormContext<RewardsConfig>();
  const { t } = useTranslation("settings");
  const enabled = useWatch({ control, name: "enabled" });

  if (!enabled) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-muted-foreground">
          {t("rewards.howCustomersEarnPoints")}
        </h4>

        {/* Points Per Dollar */}
        <FormField
          control={control}
          name="pointsPerDollar"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <div className="flex items-center gap-3">
                <SettingField
                  label={t("rewards.pointsPerDollar")}
                  helpText={t("rewards.fields.pointsPerDollar.helpText")}
                  helpDetails={
                    t("rewards.fields.pointsPerDollar.helpDetails", { returnObjects: true }) as {
                      title: string;
                      example: string;
                      benefits: string[];
                    }
                  }
                  className="text-base font-normal"
                />
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    className="w-16 text-center"
                    {...field}
                    onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 1)}
                  />
                </FormControl>
                <span className="text-base font-normal">{t("rewards.points")}</span>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Points Per Visit */}
        <FormField
          control={control}
          name="pointsPerVisit"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <div className="flex items-center gap-3">
                <SettingField
                  label={t("rewards.pointsPerVisit")}
                  helpText={t("rewards.fields.pointsPerVisit.helpText")}
                  helpDetails={
                    t("rewards.fields.pointsPerVisit.helpDetails", { returnObjects: true }) as {
                      title: string;
                      description: string;
                      benefits: string[];
                    }
                  }
                  className="text-base font-normal"
                />
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    className="w-16 text-center"
                    {...field}
                    onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 0)}
                  />
                </FormControl>
                <span className="text-base font-normal">{t("rewards.bonusPointsLabel")}</span>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Minimum Spend Threshold */}
      <div className="border-t pt-4">
        <FormField
          control={control}
          name="minimumSpendThreshold"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <SettingField
                label={t("rewards.minimumSpendTitle")}
                helpText={t("rewards.fields.minimumSpend.helpText")}
                helpDetails={
                  t("rewards.fields.minimumSpend.helpDetails", { returnObjects: true }) as {
                    title: string;
                    description: string;
                    benefits: string[];
                  }
                }
                className="text-base"
              />
              <div className="flex items-center gap-3">
                <span className="text-base font-normal">{t("rewards.minimumSpendLabel")}</span>
                <div className="flex items-center gap-1">
                  <span className="text-base">$</span>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-20 text-center"
                      {...field}
                      value={field.value / 100} // Convert cents to dollars for display
                      onChange={(e) =>
                        field.onChange(Math.round((Number.parseFloat(e.target.value) || 0) * 100))
                      } // Convert to cents
                    />
                  </FormControl>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
